/**
 * Site layout for the world pass (docs/superpowers/specs/2026-09-27-world-pass-design.md): terrain, stairs, lane,
 * buildings and groves as plain data — one source for the plan page (M1) and the scene (M2+). Pure; no three.js.
 */
import { WORLD } from '../config/world';
import { EYE, PLATFORM_EDGE_M, PLATFORM_OUTLINE, STAIRS_TOP_ALONG_M, alongAzimuth, groundY, outlineDistance, type Placement } from './composition';

export type P2 = [number, number]; // world (x, z)

const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/**
 * Natural ground (before anything is built). Seaward it falls away from the terrace's edges as steeply as the slope
 * the view was tuned on falls from its front edge (groundY measured from the edge, never higher than groundY from the
 * eye), so no flat shelf lies beside the terrace; inland it rises gently toward the hills behind; the blend widens
 * with distance so the flanks are slopes, not a step. All 360°, above sea level within 300 m.
 */
export function naturalY(x: number, z: number): number {
  const d = Math.hypot(x - EYE[0], z - EYE[2]);
  const front = -(z - EYE[2]); // metres toward the sea along the view axis
  const back = Math.max(0, -front);
  const inland = 1.2 + 0.08 * back + 5 * (1 - Math.exp(-back / 40));
  const seaward = Math.min(groundY(d), groundY(PLATFORM_EDGE_M + outlineDistance(x, z)));
  const w = smoothstep(-6 - 0.4 * d, 6 + 0.4 * d, front);
  return inland + (seaward - inland) * w;
}

/**
 * The built ground: the terrace (floor at 0, the ground under it dropped away) and the lane. TERRACE_BAND_M: the
 * margin around the terrace kept clear of the slope's vegetation (its walls and their ground strip).
 */
export const TERRACE_BAND_M = 1.2;
/** Terrain height under the terrace floor (hidden). Walls reach below where it meets the slope. */
export const UNDER_FLOOR_Y = -6;
export function surfaceY(x: number, z: number): number {
  let y = naturalY(x, z);
  const lane = laneWeight(x, z);
  if (lane > 0) y += (laneY(x, z) - y) * lane;
  const e = outlineDistance(x, z);
  // inside: under the floor; outside an 'up' edge: dug out behind the cut wall (its block covers the band)
  // under the floor the ground drops away, so grid triangles crossing a wall line slope down behind the wall (the
  // wall's ground strip, ground/terrain.ts, covers the rest)
  // (and just outside it too: a grid node barely outside a cut wall, high on the slope, would pull a sliver of ground
  // through the wall into the terrace; the wall's 1.2 m ground strip covers the dip from above)
  if (e < 0.6) return UNDER_FLOOR_Y;
  return y;
}

/** What a thing standing at (x, z) rests on: the terrace floor (0) on the terrace, else the built ground. */
export function supportY(x: number, z: number): number {
  return outlineDistance(x, z) === 0 ? 0 : surfaceY(x, z);
}

/** @deprecated name kept for the plan page: the natural ground. */
export const terrainY = naturalY;

/** A point on the torii axis: `along` metres from the eye toward the torii, `across` to its right. */
export function axisPoint(along: number, across = 0): P2 {
  const [x, , z] = alongAzimuth(WORLD.toriiAzimuthDeg, along, 0);
  const [rx, , rz] = alongAzimuth(WORLD.toriiAzimuthDeg + 90, 1, 0, [0, 0, 0]);
  return [x + rx * across, z + rz * across];
}

export const STEP_RISE_M = 0.18;
export const STEP_TREAD_M = 0.35;
export const LANDING_M = 2.0;

export interface Flight {
  steps: number;
  /** along-axis distance of the flight's top edge, and its height */
  topAlong: number;
  topY: number;
}

/** Stairs down the torii axis until they meet the ground; split into two flights with a landing when long. */
export function planStairs(): { flights: Flight[]; bottomAlong: number; bottomY: number } {
  for (let n = 1; n < 200; n++) {
    const split = n > 16 ? Math.ceil(n / 2) : n;
    const run = n * STEP_TREAD_M + (split < n ? LANDING_M : 0);
    const bottomAlong = STAIRS_TOP_ALONG_M + run;
    const bottomY = -n * STEP_RISE_M;
    const [x, z] = axisPoint(bottomAlong);
    if (bottomY <= surfaceY(x, z) + 0.05) {
      const flights: Flight[] = [{ steps: split, topAlong: STAIRS_TOP_ALONG_M, topY: 0 }];
      if (split < n) {
        flights.push({ steps: n - split, topAlong: STAIRS_TOP_ALONG_M + split * STEP_TREAD_M + LANDING_M, topY: -split * STEP_RISE_M });
      }
      return { flights, bottomAlong, bottomY };
    }
  }
  throw new Error('stairs never meet the ground');
}

/** The lane: a contour road around the hill at the stairs' foot (arc about the eye), 3.5 m wide, cut level across. */
export const LANE = { radiusM: 48, widthM: 3.5, fromAzDeg: 212, toAzDeg: 322 };
const compassAz = (x: number, z: number): number => {
  const dx = x - EYE[0];
  const dz = z - EYE[2];
  // scene: -Z is WORLD.cameraAzimuthDeg, +X is +90°
  return (WORLD.cameraAzimuthDeg + (Math.atan2(dx, -dz) * 180) / Math.PI + 360) % 360;
};
/** Height of the lane surface (the natural ground on its centre line). */
function laneY(x: number, z: number): number {
  const [cx, cz] = lanePoint(compassAz(x, z));
  return naturalY(cx, cz) + 0.02;
}
/** 1 on the lane (and 0.3 m either side), easing to 0 over 2.7 m of cut/fill bank; 0 past the lane's ends. */
export function laneWeight(x: number, z: number): number {
  const az = compassAz(x, z);
  if (az < LANE.fromAzDeg - 3 || az > LANE.toAzDeg + 3) return 0;
  const ends = smoothstep(LANE.fromAzDeg - 3, LANE.fromAzDeg, az) * (1 - smoothstep(LANE.toAzDeg, LANE.toAzDeg + 3, az));
  const dr = Math.abs(Math.hypot(x - EYE[0], z - EYE[2]) - LANE.radiusM) - LANE.widthM / 2;
  return ends * (1 - smoothstep(0.3, 3.0, dr));
}
export function lanePoint(azDeg: number, offsetM = 0): P2 {
  const [x, , z] = alongAzimuth(azDeg, LANE.radiusM + offsetM, 0);
  return [x, z];
}

export const STAIRS_PLAN = planStairs();

/** True within `margin` of the stairs' footprint (axis from the torii to the lane, full width). */
export function nearStairs(x: number, z: number, margin: number): boolean {
  const [ox, oz] = axisPoint(0);
  const [ux, uz] = axisPoint(1);
  const u: P2 = [ux - ox, uz - oz];
  const along = (x - ox) * u[0] + (z - oz) * u[1];
  const across = -(x - ox) * u[1] + (z - oz) * u[0];
  return along > STAIRS_TOP_ALONG_M - margin && along < STAIRS_PLAN.bottomAlong + margin && Math.abs(across) < 2.8 + 0.2 + margin;
}

/** Point-in-polygon on (x, z). */
export function insidePolygon([x, z]: P2, poly: P2[]): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i]!;
    const [xj, zj] = poly[j]!;
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) hit = !hit;
  }
  return hit;
}

/**
 * Low 5 m poles with lamps on the lane's downhill side, every ~25 m: their tops stay under the terrace edge in both
 * composed frames (nothing pokes up behind the fence; tested), the wires follow the lane, the lane is lit at night.
 */
export const POLE_HEIGHT_M = 5;
export const POLES: P2[] = (() => {
  const out: P2[] = [];
  const stepDeg = (25 / LANE.radiusM) * (180 / Math.PI);
  for (let az = LANE.fromAzDeg + stepDeg / 2; az < LANE.toAzDeg; az += stepDeg) out.push(lanePoint(az, LANE.widthM / 2 + 0.6));
  return out;
})();

/** Vending machine at the stairs' foot, on the lane's uphill side, right of the stairs. */
export const VENDING: P2 = lanePoint(WORLD.toriiAzimuthDeg + 5.5, -LANE.widthM / 2 - 0.5);

/** Haiden: the whole building, 5.4 m wide × 6.0 m deep; its front row is where the frame's left edge shows it. */
const HAIDEN_CORNER: P2 = [-3.8, -3.2];
export const HAIDEN = { corner: HAIDEN_CORNER, yawDeg: 45, widthM: 5.4, depthM: 6.0 };
export function haidenFootprint(): P2[] {
  const yaw = (HAIDEN.yawDeg * Math.PI) / 180;
  const local: P2[] = [[0, 0], [0, -HAIDEN.depthM], [-HAIDEN.widthM, -HAIDEN.depthM], [-HAIDEN.widthM, 0]];
  return local.map(([lx, lz]) => [
    HAIDEN.corner[0] + lx * Math.cos(yaw) + lz * Math.sin(yaw),
    HAIDEN.corner[1] - lx * Math.sin(yaw) + lz * Math.cos(yaw),
  ]);
}

/** Reserved: the honden behind the haiden (4 × 4 m). */
export const HONDEN_RESERVE: P2[] = [[-9.3, -9.6], [-12.1, -12.4], [-14.9, -9.6], [-12.1, -6.8]];

/**
 * Ema rack: under the haiden's eave, along its front face 0.3–2.1 m from the corner, 0.6–1.2 m out. In the landscape
 * frame it stands at the left edge (partly cropped) against the dark haiden: pale plaques read at a glance and do not
 * cover the view over the fence. Not in the portrait frame. 1.7 m tall.
 */
const EMA_DIR: P2 = [-Math.SQRT1_2, Math.SQRT1_2]; // along the haiden front face, away from its corner
const EMA_OUT: P2 = [Math.SQRT1_2, Math.SQRT1_2]; // the face's outward normal, toward the terrace
const emaAt = (along: number, out: number): P2 => [
  HAIDEN_CORNER[0] + EMA_DIR[0] * along + EMA_OUT[0] * out,
  HAIDEN_CORNER[1] + EMA_DIR[1] * along + EMA_OUT[1] * out,
];
export const EMA_RESERVE: P2[] = [emaAt(0.3, 0.6), emaAt(2.1, 0.6), emaAt(2.1, 1.2), emaAt(0.3, 1.2)];

/** Sakura on the terrace in a ring of earth with a stone curb. */
export const SAKURA_RING = { centre: [-5.6, 1.2] as P2, radiusM: 1.3 };

/** The terrace outline (composition.ts owns it; re-exported for the site's users). */
export const TERRACE: P2[] = PLATFORM_OUTLINE;

export type EdgeKind = 'down' | 'up';
/** Each terrace edge i (TERRACE[i] → TERRACE[i+1]): 'down' where the natural ground outside is below the floor
 * (retaining wall, fence), 'up' where it rises above it (cut wall). Decided at the edge's midpoint. */
export function terraceEdges(): Array<{ a: P2; b: P2; kind: EdgeKind; outward: P2 }> {
  return PLATFORM_OUTLINE.map((a, i) => {
    const b = PLATFORM_OUTLINE[(i + 1) % PLATFORM_OUTLINE.length]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    let outward: P2 = [(b[1] - a[1]) / len, -(b[0] - a[0]) / len];
    const mid: P2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    if (outlineDistance(mid[0] + outward[0] * 0.2, mid[1] + outward[1] * 0.2) === 0) outward = [-outward[0], -outward[1]];
    const probe = naturalY(mid[0] + outward[0] * 1.5, mid[1] + outward[1] * 1.5);
    return { a, b, kind: probe < -0.1 ? 'down' : 'up', outward };
  });
}

/**
 * Sugi (Japanese cedar) grove: wraps the terrace's left flank, up behind it and down the slope seaward-left of the
 * honden — the lobe that stands, rooted, behind the haiden at the landscape frame's left edge.
 */
export const SUGI_GROVE: P2[] = [[-18, 16], [-42, 16], [-44, -34], [-22, -42], [-11, -32], [-12, -19], [-17, -14]];

/** Outward (away from the eye) horizontal unit vector at (x, z). */
function radial([x, z]: P2): P2 {
  const len = Math.hypot(x - EYE[0], z - EYE[2]);
  return [(x - EYE[0]) / len, (z - EYE[2]) / len];
}

/**
 * Placements the site derives (on the built ground): the lane poles — crossarm and lamp across the line, the lamp
 * reaching in over the lane — and the vending machine at the stairs' foot, facing the lane.
 */
export const SITE_PLACEMENTS: Record<string, Placement> = (() => {
  const out: Record<string, Placement> = {};
  POLES.forEach((p, i) => {
    const [rx, rz] = radial(p);
    // local +X outward: the lamp (at local -X) hangs in over the lane
    out[`pole_${i}`] = { asset: 'utility_pole', position: [p[0], surfaceY(p[0], p[1]), p[1]], rotationYDeg: (Math.atan2(-rz, rx) * 180) / Math.PI, footprintM: 0.3 };
  });
  const [vx, vz] = radial(VENDING);
  // local +Z (its front) outward, toward the lane beside it
  out.vending = { asset: 'vending', position: [VENDING[0], surfaceY(VENDING[0], VENDING[1]), VENDING[1]], rotationYDeg: (Math.atan2(vx, vz) * 180) / Math.PI, footprintM: 0.6 };
  return out;
})();

export const POLE_KEYS = POLES.map((_, i) => `pole_${i}`);
